import React from 'react';
import type { PartnerLogosComponent } from '../../../types/builder';

interface PartnerLogosRendererProps {
  component: PartnerLogosComponent;
}

export const PartnerLogosRenderer: React.FC<PartnerLogosRendererProps> = ({ component }) => {
  const { title, logos, logoHeight, animationSpeed, backgroundColor, padding, style } = component.settings;

  const partnerStyle = style || 'default';

  // Split logos into two rows
  const midPoint = Math.ceil(logos.length / 2);
  const firstRowLogos = logos.slice(0, midPoint);
  const secondRowLogos = logos.slice(midPoint);

  // Duplicate logos for infinite scroll effect
  const firstRowDuplicated = [...firstRowLogos, ...firstRowLogos, ...firstRowLogos];
  const secondRowDuplicated = [...secondRowLogos, ...secondRowLogos, ...secondRowLogos];

  const renderLogo = (logo: any, index: number) => (
    <div
      key={`${logo.id}-${index}`}
      className="flex-shrink-0 px-3 flex items-center justify-center"
      style={{ height: `${logoHeight}px` }}
    >
      {logo.link ? (
        <a href={logo.link} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={logo.imageUrl}
            alt={logo.name}
            style={{ height: `${logoHeight}px`, width: 'auto', maxWidth: '200px' }}
            className="object-contain opacity-70 hover:opacity-100 transition-opacity filter grayscale hover:grayscale-0"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x80?text=' + encodeURIComponent(logo.name);
            }}
          />
        </a>
      ) : (
        <img
          src={logo.imageUrl}
          alt={logo.name}
          style={{ height: `${logoHeight}px`, width: 'auto', maxWidth: '200px' }}
          className="object-contain opacity-70 filter grayscale"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x80?text=' + encodeURIComponent(logo.name);
          }}
        />
      )}
    </div>
  );

  // Default Style
  if (partnerStyle === 'default') {
    return (
      <div style={{ backgroundColor, padding: `${padding}px 0`, overflow: 'hidden' }}>
        {title && (
          <h2 className="text-xl font-bold text-center mb-6" style={{ color: '#1f2937' }}>
            {title}
          </h2>
        )}
        
        {/* First Row - Scroll Right */}
        <div className="mb-4 overflow-hidden">
          <div
            className="flex"
            style={{
              animation: `scrollRight ${animationSpeed}s linear infinite`,
            }}
          >
            {firstRowDuplicated.map(renderLogo)}
          </div>
        </div>

        {/* Second Row - Scroll Left */}
        <div className="overflow-hidden">
          <div
            className="flex"
            style={{
              animation: `scrollLeft ${animationSpeed}s linear infinite`,
            }}
          >
            {secondRowDuplicated.map(renderLogo)}
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes scrollRight {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-33.333%); }
          }
          @keyframes scrollLeft {
            0% { transform: translateX(-33.333%); }
            100% { transform: translateX(0%); }
          }
        `}</style>
      </div>
    );
  }

  // Dark Style
  if (partnerStyle === 'dark') {
    return (
      <div style={{ backgroundColor: backgroundColor || '#0f172a', padding: `${padding}px 0`, overflow: 'hidden' }}>
        {title && (
          <h2 className="text-xl font-bold text-center mb-6 text-white">
            {title}
          </h2>
        )}
        
        <div className="mb-4 overflow-hidden">
          <div className="flex" style={{ animation: `scrollRight ${animationSpeed}s linear infinite` }}>
            {firstRowDuplicated.map(renderLogo)}
          </div>
        </div>

        <div className="overflow-hidden">
          <div className="flex" style={{ animation: `scrollLeft ${animationSpeed}s linear infinite` }}>
            {secondRowDuplicated.map(renderLogo)}
          </div>
        </div>

        <style>{`
          @keyframes scrollRight {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-33.333%); }
          }
          @keyframes scrollLeft {
            0% { transform: translateX(-33.333%); }
            100% { transform: translateX(0%); }
          }
        `}</style>
      </div>
    );
  }

  // Light Style
  if (partnerStyle === 'light') {
    return (
      <div style={{ backgroundColor: backgroundColor || '#f8fafc', padding: `${padding}px 0`, overflow: 'hidden', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        {title && (
          <h2 className="text-xl font-bold text-center mb-6" style={{ color: '#1f2937' }}>
            {title}
          </h2>
        )}
        
        <div className="mb-4 overflow-hidden">
          <div className="flex" style={{ animation: `scrollRight ${animationSpeed}s linear infinite` }}>
            {firstRowDuplicated.map(renderLogo)}
          </div>
        </div>

        <div className="overflow-hidden">
          <div className="flex" style={{ animation: `scrollLeft ${animationSpeed}s linear infinite` }}>
            {secondRowDuplicated.map(renderLogo)}
          </div>
        </div>

        <style>{`
          @keyframes scrollRight {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-33.333%); }
          }
          @keyframes scrollLeft {
            0% { transform: translateX(-33.333%); }
            100% { transform: translateX(0%); }
          }
        `}</style>
      </div>
    );
  }

  // Gradient Style
  if (partnerStyle === 'gradient') {
    return (
      <div style={{ background: backgroundColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: `${padding}px 0`, overflow: 'hidden' }}>
        {title && (
          <h2 className="text-xl font-bold text-center mb-6 text-white">
            {title}
          </h2>
        )}
        
        <div className="mb-4 overflow-hidden">
          <div className="flex" style={{ animation: `scrollRight ${animationSpeed}s linear infinite` }}>
            {firstRowDuplicated.map(renderLogo)}
          </div>
        </div>

        <div className="overflow-hidden">
          <div className="flex" style={{ animation: `scrollLeft ${animationSpeed}s linear infinite` }}>
            {secondRowDuplicated.map(renderLogo)}
          </div>
        </div>

        <style>{`
          @keyframes scrollRight {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-33.333%); }
          }
          @keyframes scrollLeft {
            0% { transform: translateX(-33.333%); }
            100% { transform: translateX(0%); }
          }
        `}</style>
      </div>
    );
  }

  // Fallback
  return null;
};

